"use client";

import Link from "next/link";
import { ArrowRight, LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface HeroStat {
  label: string;
  value: string;
  icon: LucideIcon;
  accent: string;
}

export interface HeroSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  trustLine?: string;
  ctaPrimary: { label: string; href: string };
  ctaSecondary?: { label: string; href: string };
  trustIndicators?: string[];
  stats?: HeroStat[];
  primaryAccent?: string;
  secondaryAccent?: string;
  trustIconMap?: Record<string, LucideIcon>;
  introBadges?: string[];
}

const DEFAULT_ACCENTS = [
  "var(--color-crimson)",
  "var(--color-purple)",
  "var(--color-cyan)",
  "var(--color-emerald)",
  "var(--color-amber)",
] as const;

export function HeroSection({
  id = "top",
  eyebrow,
  title,
  titleAccent,
  lede,
  trustLine,
  ctaPrimary,
  ctaSecondary,
  trustIndicators = [],
  stats = [],
  primaryAccent = "var(--color-crimson)",
  secondaryAccent = "var(--color-purple)",
  trustIconMap,
  introBadges = [],
}: HeroSectionProps) {
  return (
    <Section id={id} className="relative overflow-hidden pt-12 pb-16 sm:pt-20 sm:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full opacity-30 blur-[140px] animate-pulse-glow"
          style={{ background: primaryAccent }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 h-72 w-72 rounded-full opacity-25 blur-[120px] animate-pulse-glow"
          style={{ background: secondaryAccent, animationDelay: "2s" }}
        />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-4xl">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6"
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: primaryAccent }} />
              <span className="eyebrow-tiny" style={{ color: primaryAccent }}>
                {eyebrow}
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <h1 className="display-hero text-balance">
            {title}{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: `linear-gradient(135deg, ${primaryAccent}, ${secondaryAccent}, var(--color-cyan))`,
              }}
            >
              {titleAccent}
            </span>
          </h1>
        </Reveal>

        <Reveal delay={160}>
          <p className="mt-6 max-w-3xl text-body leading-relaxed text-[rgb(var(--fg-muted))]">
            {lede}
          </p>
        </Reveal>

        {trustLine && (
          <Reveal delay={220}>
            <p className="mt-4 text-sm text-[rgb(var(--fg-subtle))] font-semibold">
              {trustLine}
            </p>
          </Reveal>
        )}

        {introBadges.length > 0 && (
          <Reveal delay={240}>
            <div className="mt-6 flex flex-wrap gap-2">
              {introBadges.map((b) => (
                <span
                  key={b}
                  className="px-3 py-1.5 rounded-full glass text-xs font-semibold"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${primaryAccent} 18%, transparent)`,
                  }}
                >
                  {b}
                </span>
              ))}
            </div>
          </Reveal>
        )}

        <Reveal delay={300}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              href={ctaPrimary.href}
              className="btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl"
              style={{ boxShadow: `0 25px 50px -12px color-mix(in oklch, ${primaryAccent} 40%, transparent)` }}
            >
              {ctaPrimary.label} <ArrowRight className="h-4 w-4" />
            </Link>
            {ctaSecondary && (
              <Link
                href={ctaSecondary.href}
                className="btn-glass h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base"
              >
                {ctaSecondary.label}
              </Link>
            )}
          </div>
        </Reveal>

        {stats.length > 0 && (
          <Reveal delay={340}>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-5xl">
              {stats.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.label}
                    className="glass rounded-2xl p-4"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="h-3.5 w-3.5" style={{ color: s.accent }} />
                    </div>
                    <div className="text-lg font-bold" style={{ color: s.accent }}>
                      {s.value}
                    </div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[rgb(var(--fg-subtle))] leading-tight mt-1">
                      {s.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        )}

        {trustIndicators.length > 0 && (
          <Reveal delay={360}>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 max-w-5xl">
              {trustIndicators.map((indicator, i) => {
                const accent = DEFAULT_ACCENTS[i % DEFAULT_ACCENTS.length]!;
                const Icon = trustIconMap?.[indicator] ?? (stats[i]?.icon);
                return (
                  <div
                    key={indicator}
                    className="glass rounded-2xl p-4 flex items-center gap-3"
                  >
                    <span
                      className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                      style={{
                        background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                      }}
                    >
                      {Icon ? <Icon className="h-4 w-4" style={{ color: accent }} /> : null}
                    </span>
                    <span className="text-xs font-semibold leading-snug text-[rgb(var(--fg))]">
                      {indicator}
                    </span>
                  </div>
                );
              })}
            </div>
          </Reveal>
        )}
      </Container>
    </Section>
  );
}
